import type { FC } from "react";
import { useCallback } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Popover from "@mui/material/Popover";
import SvgIcon from "@mui/material/SvgIcon";
import Typography from "@mui/material/Typography";
import UsersPlusIcon from "src/icons/untitled-ui/duocolor/users-plus";
import Settings01Icon from "@untitled-ui/icons-react/build/esm/Settings01";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

import { RouterLink } from "src/components/router-link";
import { useAuth } from "src/hooks/use-auth";
import { useRouter } from "src/hooks/use-router";
import { paths } from "src/paths";
import { Issuer } from "src/utils/auth";
import useHousehold, { ExperienceFlag } from "src/contexts/household-context";
import { useEstatePlanSummaryStore } from "src/hooks/snug/use-summary";
import useUserDataProvider from "src/contexts/userData-context";

interface AccountPopoverProps {
  anchorEl: null | Element;
  onClose?: () => void;
  open?: boolean;
  express?: boolean;
}

export const AccountPopover: FC<AccountPopoverProps> = (props) => {
  const { anchorEl, onClose, open, express, ...other } = props;
  const router = useRouter();
  const auth = useAuth();
  const { experienceFlag } = useHousehold();
  const { userData } = useUserDataProvider();

  const handleLogout = useCallback(async (): Promise<void> => {
    try {
      onClose?.();

      switch (auth.issuer) {
        case Issuer.JWT: {
          await auth.signOut();
          break;
        }

        default: {
          console.warn("Using an unknown Auth Issuer, did not log out");
        }
      }

      router.push(
        paths.auth.jwt.entryStart + "?returnTo=" + window.location.pathname
      );
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong!");
    }
  }, [auth, router, onClose]);

  const { handleCreateNewSummaryAndPoll } =
    useEstatePlanSummaryStore(userData?.ud_id || null);

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{
        horizontal: "center",
        vertical: "bottom",
      }}
      disableScrollLock
      onClose={onClose}
      open={!!open}
      PaperProps={{ sx: { width: 200 } }}
      {...other}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="body1">{userData?.full_name}</Typography>
        <Typography color="text.secondary" variant="body2">
          {userData?.contact_email}
        </Typography>
      </Box>
      <Divider />
      
      <Box sx={{ p: 1 }}>
        
        <ListItemButton
          component={RouterLink}
          href={express ? `${paths.dashboard.express}/account` : paths.dashboard.account}
          onClick={onClose}
          sx={{
            borderRadius: 1,
            px: 1,
            py: 0.5,
          }}
        >
          <ListItemIcon>
            <SvgIcon fontSize="small">
              <Settings01Icon />
            </SvgIcon>
          </ListItemIcon>
          <ListItemText
            primary={<Typography variant="body1">Profile</Typography>}
          />
        </ListItemButton>
        {experienceFlag !== ExperienceFlag.READONLY && !express && (
          <ListItemButton
            component={RouterLink}
            href={paths.dashboard.accountSharing("sharing")}
            onClick={onClose}
            sx={{
              borderRadius: 1,
              px: 1,
              py: 0.5,
            }}
          >
            <ListItemIcon>
              <SvgIcon fontSize="small">
                <UsersPlusIcon />
              </SvgIcon>
            </ListItemIcon>
            <ListItemText
              primary={<Typography variant="body1">Sharing</Typography>}
            />
          </ListItemButton>
        )}
        {experienceFlag !== ExperienceFlag.READONLY && !express && (
          <ListItemButton
            onClick={() => {
              handleCreateNewSummaryAndPoll();
              onClose?.();
            }}
            sx={{
              borderRadius: 1,
              px: 1,
              py: 0.5,
            }}
          >
            <ListItemIcon>
              <SvgIcon fontSize="small">
                <FileDownloadOutlinedIcon />
              </SvgIcon>
            </ListItemIcon>
            <ListItemText
              primary={<Typography variant="body1">Download plan</Typography>}
            />
          </ListItemButton>
        )}
        </Box>
        <Divider/>
        <Box sx={{ p: 1 }}>
        <ListItemButton
          component="a"
          href="https://s3.us-east-2.amazonaws.com/assets.getsnug.com/files/User+Terms+of+Service.pdf"
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClose}
          sx={{
            borderRadius: 1,
            px: 1,
            py: 0.5,
          }}
        >
          
          <ListItemText
            primary={<Typography variant="body2">Terms of service</Typography>}
          />
        </ListItemButton>
      </Box>

      <Divider sx={{ my: "0 !important" }} />
      <Box
        sx={{
          display: "flex",
          p: 1,
          justifyContent: "center",
        }}
      >
        <Button color="inherit" onClick={handleLogout} size="small">
          Logout
        </Button>
      </Box>
    </Popover>
  );
};

AccountPopover.propTypes = {
  anchorEl: PropTypes.any,
  onClose: PropTypes.func,
  open: PropTypes.bool,
};
